import { addDays, endCandidates } from '@felix/domain';
import { z } from 'zod';
import { asClock, type ShipTime, wallClock, wallToInstants } from './ship-clock.js';
import type { WorkPeriod, WorkPeriodInput } from './types.js';

const MINUTE = 60_000;

/**
 * Çalışma dönemi türleri; sunucudaki `WORK_ACTIVITIES` (`@felix/domain`) ile aynı sıra. Sunucu
 * türü yine kendisi denetler (`INVALID_ACTIVITY`).
 */
export const WORK_ACTIVITY_OPTIONS = [
  'watch',
  'anchor_watch',
  'maneuvering',
  'port_operations',
  'cargo',
  'maintenance',
  'drill',
  'emergency',
  'other',
] as const;

const time = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'restHours.edit.errors.VALIDATION_FAILED');

/**
 * Dönem formu (M10d2): gün ve saatler gemi saatiyle girilir. Bitiş başlangıçtan önce ya da
 * gece yarısındaysa ertesi gündür. Sunucudaki kuralların arayüzdeki karşılığı; sunucu yine
 * kendisi doğrular (çakışma, görev, kilit).
 */
export const workPeriodSchema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'restHours.edit.errors.VALIDATION_FAILED'),
    startTime: time,
    endTime: time,
    activity: z.enum(WORK_ACTIVITY_OPTIONS, 'restHours.edit.errors.VALIDATION_FAILED'),
    location: z.string().trim().max(100, 'restHours.edit.errors.TOO_LONG'),
    note: z.string().trim().max(1000, 'restHours.edit.errors.TOO_LONG'),
    /**
     * Saat geri alınan günde tekrar eden saatin hangisi (1. ya da 2.; ADR 0065 §7). Saat tekrar
     * etmiyorsa yok sayılır.
     */
    startOccurrence: z.union([z.literal(1), z.literal(2)]).optional(),
    endOccurrence: z.union([z.literal(1), z.literal(2)]).optional(),
  })
  // Acil durum gerekçeyle kaydedilir [MLC A2.3 §14; ADR 0018 §3]
  .refine((v) => v.activity !== 'emergency' || v.note.trim() !== '', {
    path: ['note'],
    message: 'restHours.edit.errors.EMERGENCY_REASON_REQUIRED',
  });
export type WorkPeriodValues = z.infer<typeof workPeriodSchema>;

const minutesOf = (value: string) => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));

export type WorkPeriodConversion =
  | { ok: true; input: WorkPeriodInput }
  | { ok: false; field: keyof WorkPeriodValues; code: string };

/**
 * Girilen saatlerin anları (ADR 0065 §7): başlangıç o günün duvar saati (yaşanmadıysa yok, tekrar
 * ediyorsa iki); bitiş başlangıçtan sonra saatin o değeri ilk gösterdiği an (tekrar ediyorsa iki).
 * Form, iki aday varsa "hangisi" diye sorar.
 */
export function periodChoices(
  values: Pick<WorkPeriodValues, 'date' | 'startTime' | 'endTime' | 'startOccurrence'>,
  time: ShipTime,
): { starts: number[]; ends: number[] } {
  const timeOk = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(values.date) || !timeOk(values.startTime)) {
    return { starts: [], ends: [] };
  }
  const starts = wallToInstants(time, values.date, minutesOf(values.startTime));
  const start = starts[Math.min((values.startOccurrence ?? 1) - 1, starts.length - 1)];
  if (start === undefined || !timeOk(values.endTime)) return { starts, ends: [] };
  return {
    starts,
    ends: endCandidates(asClock(time), start, values.date, minutesOf(values.endTime)),
  };
}

/** Formdan sunucu gövdesi: gemi yerel saati → UTC. "Gelecek" gemi sunucusu saatine göre. */
export function toWorkPeriodInput(
  values: WorkPeriodValues,
  ship: { timeZone: ShipTime; now: string },
): WorkPeriodConversion {
  const { starts, ends } = periodChoices(values, ship.timeZone);
  // Saat ileri alınan günde yaşanmayan saat girilemez (ADR 0065 §7)
  const startAt = starts[Math.min((values.startOccurrence ?? 1) - 1, starts.length - 1)];
  if (startAt === undefined) return { ok: false, field: 'startTime', code: 'TIME_SKIPPED' };
  const endAt = ends[Math.min((values.endOccurrence ?? 1) - 1, ends.length - 1)];
  if (endAt === undefined) return { ok: false, field: 'endTime', code: 'TIME_SKIPPED' };
  // Aynı saat bir gün sonrası demektir: 24 saatlik dönem girilmez (tekrar eden saatin 2.si hariç)
  if (endAt - startAt >= 24 * 60 * MINUTE && values.startTime === values.endTime) {
    return { ok: false, field: 'endTime', code: 'END_NOT_AFTER_START' };
  }
  const start = new Date(startAt).toISOString();
  const end = new Date(endAt).toISOString();
  if (end > ship.now) return { ok: false, field: 'endTime', code: 'FUTURE_DATE' };
  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());
  return {
    ok: true,
    input: {
      start,
      end,
      activity: values.activity,
      location: orNull(values.location),
      note: orNull(values.note),
    },
  };
}

/** Gemi saatiyle gün ve saat (`HH:MM`); tekrar eden saatte oluşumu. */
export function localTime(
  at: string,
  time: ShipTime,
): { date: string; time: string; occurrence: 1 | 2 | null } {
  return wallClock(time, Date.parse(at));
}

/** Düzeltilecek dönemin form değerleri. */
export function fromWorkPeriod(period: WorkPeriod, timeZone: ShipTime): WorkPeriodValues {
  const start = localTime(period.start, timeZone);
  const end = localTime(period.end, timeZone);
  const activity = (WORK_ACTIVITY_OPTIONS as readonly string[]).includes(period.activity)
    ? (period.activity as WorkPeriodValues['activity'])
    : 'other';
  return {
    date: start.date,
    startTime: start.time,
    endTime: end.time,
    activity,
    location: period.location ?? '',
    note: period.note ?? '',
    ...(start.occurrence ? { startOccurrence: start.occurrence } : {}),
    ...(end.occurrence ? { endOccurrence: end.occurrence } : {}),
  };
}

/** Bitişin günü (başlangıç gününden sonraysa ertesi gün): tabloda "+1". */
export function endsNextDay(values: Pick<WorkPeriodValues, 'date'>, end: number, time: ShipTime) {
  return wallClock(time, end).date === addDays(values.date, 1);
}
